import React, { useState } from "react"
import type { FormProps } from "antd"
import { Flex } from "antd"
import { Form, Input } from "antd"
import { useTranslation } from "react-i18next"
import { encryptData, transformKey } from "../../utils/crypto"
import { useEncryptedData } from "../../context/EncryptedData"
import { Button } from "../Button"
import {
  DUMMY_FORM_VALUE,
  STORAGE_ENCRYPTED_DATA,
  STORAGE_IV,
} from "../../constants"
import { deleteStorageVariable, getStorageVariable } from "../../utils/storage"
import { login } from "../../services/login"
import { PASSWORD, USERNAME } from "./constants"
import { getKey } from "../../services/getKey"
import { useNotification } from "../../context/Notification"

type Props = {
  page: string
}

type FieldType = {
  username: string
  password: string
}

export const LoginForm: React.FC<Props> = ({ page }) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { setEncryptedData } = useEncryptedData()
  const [isDeleteBtnDisabled, setDeleteBtnDisabled] = useState(
    getStorageVariable(`${page}${STORAGE_ENCRYPTED_DATA}`) === ""
  )
  const [isLoading, setLoading] = useState(false)
  const { success, error } = useNotification()

  const initialValues: FieldType = {
    username: isDeleteBtnDisabled ? "" : DUMMY_FORM_VALUE,
    password: isDeleteBtnDisabled ? "" : DUMMY_FORM_VALUE,
  }

  const onFinish: FormProps<FieldType>["onFinish"] = async (values) => {
    setLoading(true)

    if (!values) {
      alert("Data are missing!")
      setLoading(false)
      return
    }

    const stringData = JSON.stringify(values)
    const response = await login(page, values)

    if (!response.ok) {
      error(
        t("login.notifications.error.message"),
        t("login.notifications.error.invalidCredentials")
      )
      setLoading(false)
      return
    }

    const keyResponse = await getKey()

    if (!keyResponse.key) {
      error(
        t("login.notifications.error.message"),
        t("login.notifications.error.noKey")
      )
      setLoading(false)
      return
    }

    const key = await transformKey(keyResponse.key)
    const { encryptedData, iv } = await encryptData(key, stringData)

    setEncryptedData(encryptedData, iv, page)
    form.setFieldValue(USERNAME, DUMMY_FORM_VALUE)
    form.setFieldValue(PASSWORD, DUMMY_FORM_VALUE)
    success(
      t("login.notifications.success.message"),
      t("login.notifications.success.description")
    )

    setDeleteBtnDisabled(false)
    setLoading(false)
  }

  const onFinishFailed: FormProps<FieldType>["onFinishFailed"] = (
    errorInfo
  ) => {
    console.log("Failed:", errorInfo)
  }

  const deleteHandler = () => {
    deleteStorageVariable(`${page}${STORAGE_ENCRYPTED_DATA}`)
    deleteStorageVariable(`${page}${STORAGE_IV}`)
    setDeleteBtnDisabled(true)
    form.setFieldValue(USERNAME, "")
    form.setFieldValue(PASSWORD, "")
  }

  const getSubmitBtnLabel = () => {
    if (isLoading) return t("login.checking")
    if (isDeleteBtnDisabled) return t("login.login")
    return t("login.changeData")
  }

  return (
    <Form
      id={page}
      name={`${page}LoginForm`}
      form={form}
      style={{ maxWidth: 600, marginTop: 20 }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
      layout="vertical"
      initialValues={initialValues}
    >
      <Form.Item<FieldType>
        label={t("login.username")}
        name={USERNAME}
        rules={[{ required: true, message: t("login.usernameEmptyError") }]}
      >
        <Input id={`${page}Username`} />
      </Form.Item>

      <Form.Item<FieldType>
        label={t("login.password")}
        name={PASSWORD}
        rules={[{ required: true, message: t("login.passwordEmptyError") }]}
      >
        <Input.Password id={`${page}Password`} />
      </Form.Item>

      <Form.Item>
        <Flex justify="space-between">
          <Button
            label={getSubmitBtnLabel()}
            type="primary"
            htmlType="submit"
            loading={isLoading}
          />
          <Button
            label={t("login.delete")}
            htmlType="button"
            style={{ marginLeft: 10 }}
            disabled={isDeleteBtnDisabled}
            onClick={deleteHandler}
          />
        </Flex>
      </Form.Item>
    </Form>
  )
}
